import type { MaintenanceProtocolArchiveDetail } from '@gb-ticket/shared-types';
import { ChevronDownIcon } from 'lucide-react';
import { useId, useState } from 'react';
import { Link, useParams } from 'react-router';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { usePageTitle } from '@/hooks/use-page-title';
import { API_BASE_URL } from '@/lib/api';
import { PlatformPageHeader } from '@/platform/components/platform-page-header';
import { MaintenanceStatusBadge } from '@/products/maintenance/features/maintenance-status-badge';
import { TechnicianNotesCard } from '@/products/maintenance/features/protocol/technician-notes-card';
import { useMaintenanceProtocolArchiveDetail } from '@/products/maintenance/features/protocol/use-maintenance-protocol';
import { maintenanceInstallationPath } from '@/products/maintenance/maintenance-paths';

export function MaintenanceProtocolArchivePage() {
  const { protocolId = '' } = useParams();
  const detail = useMaintenanceProtocolArchiveDetail(protocolId);
  usePageTitle(detail.data?.orderNumber ?? 'Wartungsprotokoll');

  if (detail.isPending) {
    return <p className="text-sm text-muted-foreground">Protokoll wird geladen …</p>;
  }
  if (detail.isError || !detail.data) {
    return <p className="text-sm text-destructive">Das Protokoll wurde nicht gefunden.</p>;
  }

  const row = detail.data;
  const pdfUrl = `${API_BASE_URL}/maintenance/protocols/${row.id}/final.pdf`;

  return (
    <div className="mx-auto w-full max-w-2xl space-y-6">
      <PlatformPageHeader
        title={row.orderNumber}
        description={`${row.templateSnapshot.templateName} v${row.templateSnapshot.versionNumber}`}
        action={<MaintenanceStatusBadge status={row.workOrderStatus} />}
      />
      <p className="text-sm">
        <Link
          className="text-primary hover:underline"
          to={maintenanceInstallationPath(row.installationSnapshot.id)}
        >
          ← Zur Anlage
        </Link>
      </p>

      <div className="grid gap-4 sm:grid-cols-2">
        <ArchiveSnapshotCard title="Kunde">
          {row.customerSnapshot.name}
          <br />
          {row.customerSnapshot.addressLine1}
          <br />
          {row.customerSnapshot.postalCode} {row.customerSnapshot.city}
        </ArchiveSnapshotCard>
        <ArchiveSnapshotCard title="Projekt">
          {row.projectSnapshot.name}
          <br />
          {row.projectSnapshot.siteLine1}
          <br />
          {row.projectSnapshot.sitePostalCode} {row.projectSnapshot.siteCity}
        </ArchiveSnapshotCard>
        <ArchiveSnapshotCard title="Anlage">
          {row.installationSnapshot.installationNumber} · {row.installationSnapshot.name}
          <br />
          {row.installationSnapshot.location}
        </ArchiveSnapshotCard>
        <ArchiveSnapshotCard title="Abschluss">
          {row.completedAt ? new Date(row.completedAt).toLocaleString('de-DE') : 'Noch offen'}
          {row.finalPdfSha256 ? (
            <>
              <br />
              <span className="text-xs text-muted-foreground">SHA-256</span>
              <br />
              <span className="font-mono text-xs break-all">{row.finalPdfSha256}</span>
            </>
          ) : null}
        </ArchiveSnapshotCard>
      </div>

      <TechnicianNotesCard notes={row.generalNotes} />

      {row.hasFinalPdf ? (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-3 space-y-0">
            <CardTitle className="text-base">Abschluss-PDF</CardTitle>
            <Button asChild size="sm" className="min-h-11">
              <a href={pdfUrl} download>
                PDF herunterladen
              </a>
            </Button>
          </CardHeader>
          <CardContent>
            <iframe
              title={`Abschluss-PDF ${row.orderNumber}`}
              src={pdfUrl}
              className="h-[70vh] w-full rounded-md border"
            />
          </CardContent>
        </Card>
      ) : (
        <p className="text-sm text-muted-foreground">Noch kein Abschluss-PDF verfügbar.</p>
      )}

      <ArchiveEventLogSection events={row.events} />
    </div>
  );
}

function ArchiveEventLogSection({
  events,
}: {
  events: MaintenanceProtocolArchiveDetail['events'];
}) {
  const [open, setOpen] = useState(false);
  const contentId = useId();

  return (
    <Card className="gap-0 overflow-hidden py-0">
      <CardHeader className={cn('pb-3', open && 'border-b')}>
        <button
          type="button"
          className="flex w-full items-center gap-2 rounded-sm text-left focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
          aria-expanded={open}
          aria-controls={contentId}
          onClick={() => setOpen((value) => !value)}
        >
          <CardTitle className="min-w-0 flex-1 text-base">Ereignisverlauf</CardTitle>
          <span className="shrink-0 text-sm text-muted-foreground tabular-nums">
            {events.length}
          </span>
          <ChevronDownIcon
            className={cn(
              'size-4 shrink-0 text-muted-foreground transition-transform',
              open && 'rotate-180',
            )}
            aria-hidden
          />
        </button>
      </CardHeader>
      {open ? (
        <CardContent id={contentId} className="space-y-2">
          {events.map((event) => (
            <div key={event.id} className="rounded-md border p-3 text-sm">
              <p className="font-medium">{event.type}</p>
              <p className="text-muted-foreground">
                {new Date(event.at).toLocaleString('de-DE')} · {event.actor.displayName}
              </p>
            </div>
          ))}
        </CardContent>
      ) : null}
    </Card>
  );
}

function ArchiveSnapshotCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold text-muted-foreground">{title}</CardTitle>
      </CardHeader>
      <CardContent className="text-sm">{children}</CardContent>
    </Card>
  );
}
